import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { buildingsApi } from '../api/buildingsApi';
import { scheduledTasksApi, ScheduledTask, TriggerResponse } from '../services/scheduledTasksApi';

interface Building {
  id: string;
  name: string;
  serviceFeeDay: number;
  autoGenerateMonthlyServiceFees: boolean;
  autoSendEmailReminder: boolean;
  paymentFromDay: number;
  paymentToDay: number;
}

export const AutomationSettings: React.FC = () => {
  // const { t } = useLang(); // Removed unused variable
  // const { user } = useAuth(); // Removed unused variable
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [buildingsData, tasksData] = await Promise.all([
        buildingsApi.getBuildings(),
        scheduledTasksApi.getScheduledTasks(),
      ]);
      setBuildings(buildingsData);
      setScheduledTasks(tasksData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerServiceFeeGeneration = async (buildingId: string) => {
    try {
      setTriggering(buildingId);
      const result: TriggerResponse = await scheduledTasksApi.triggerMonthlyServiceFeeGeneration(buildingId);
      
      if (result.success) {
        alert('Tạo phí dịch vụ hàng tháng thành công!');
        // Reload tasks to get updated status
        const tasksData = await scheduledTasksApi.getScheduledTasks();
        setScheduledTasks(tasksData);
      } else {
        alert(`Lỗi: ${result.message}`);
      }
    } catch (error) {
      console.error('Failed to trigger service fee generation:', error);
      alert('Có lỗi xảy ra khi tạo phí dịch vụ');
    } finally {
      setTriggering(null);
    }
  };

  const getTaskStatus = (buildingId: string) => {
    const task = scheduledTasks.find(t => t.buildingId === buildingId && t.type === 'MONTHLY_SERVICE_FEE');
    return task;
  };

  const getStatusInfo = (status?: string) => {
    switch (status) {
      case 'COMPLETED':
        return { text: 'Hoàn thành', class: 'bg-green-100 text-green-800' };
      case 'ACTIVE':
        return { text: 'Hoạt động', class: 'bg-blue-100 text-blue-800' };
      case 'FAILED':
        return { text: 'Thất bại', class: 'bg-red-100 text-red-800' };
      case 'INACTIVE':
        return { text: 'Không hoạt động', class: 'bg-gray-100 text-gray-800' };
      default:
        return { text: 'Chưa thiết lập', class: 'bg-yellow-100 text-yellow-800' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">TIỆN ÍCH</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">Cài đặt tự động hóa</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cài đặt tự động hóa</h1>
          <p className="text-gray-600 mt-1">Quản lý tự động tạo phí dịch vụ hàng tháng và gửi email thông báo</p>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Thông tin quan trọng</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Hệ thống sẽ tự động tạo phí dịch vụ hàng tháng vào ngày được cài đặt trong "Ngày chốt phí dịch vụ"</li>
                <li>Nếu bật "Tự động gửi email báo phí dịch vụ", hệ thống sẽ gửi email thông báo đến từng căn hộ</li>
                <li>Bạn có thể kích hoạt thủ công bằng nút "Tạo phí ngay" bên dưới</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Buildings Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Danh sách tòa nhà</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tòa nhà
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ngày chốt phí
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kỳ thanh toán
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tự động tạo phí
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gửi email tự động
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lần chạy cuối
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {buildings.map((building) => {
                const task = getTaskStatus(building.id);
                const statusInfo = getStatusInfo(task?.status);
                
                return (
                  <tr key={building.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{building.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Ngày {building.serviceFeeDay}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {building.paymentFromDay} - {building.paymentToDay}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        building.autoGenerateMonthlyServiceFees !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {building.autoGenerateMonthlyServiceFees !== false ? 'Bật' : 'Tắt'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        building.autoSendEmailReminder
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {building.autoSendEmailReminder ? 'Bật' : 'Tắt'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.class}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task?.lastRun 
                        ? new Date(task.lastRun).toLocaleDateString('vi-VN')
                        : 'Chưa chạy'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleTriggerServiceFeeGeneration(building.id)}
                        disabled={triggering === building.id}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {triggering === building.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Đang tạo...
                          </>
                        ) : (
                          'Tạo phí ngay'
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task History */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Lịch sử tác vụ tự động</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tòa nhà
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loại tác vụ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lần chạy cuối
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lần chạy tiếp theo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scheduledTasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Chưa có lịch sử tác vụ
                  </td>
                </tr>
              ) : (
                scheduledTasks.map((task) => {
                  const statusInfo = getStatusInfo(task.status);
                  
                  return (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {task.building?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {task.type === 'MONTHLY_SERVICE_FEE' ? 'Tạo phí hàng tháng' : task.type}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.class}`}>
                          {statusInfo.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {task.lastRun 
                          ? new Date(task.lastRun).toLocaleString('vi-VN')
                          : 'Chưa chạy'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {task.nextRun 
                          ? new Date(task.nextRun).toLocaleString('vi-VN')
                          : 'Chưa lên lịch'
                        }
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}; 